import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { JsonFailure } from '@skynet/data-ws-core';
import { registerErrorMessage, isOkCommand, parseResponse } from '@skynet/data-ws-core';
import type {
  BalanceHistoryState,
  JsonBalanceHistorySuccess,
  JsonPriceHistorySuccess,
  SetObjInitialBalanceFormValues,
} from 'src/store/shared/balance-history/types';
import { BalanceHistoryCommands } from 'src/store/shared/balance-history/types';
import dayjs from '@skynet/formatting-date';
import {
  getComparisonTimeOptionValue,
  isComparisonDateNow,
} from 'src/store/shared/balance-history/helpers';
import { getBalanceHistoryWs, setInitialBalance } from 'src/store/shared/balance-history/actions';
import type { SliceFactoryPropsWithMessages } from 'src/store/shared/types';

registerErrorMessage(BalanceHistoryCommands.SetInitialBalance, {
  title: 'Set Initial Balance Error',
  getDescription: (json: JsonFailure) => String(json.message),
});

export const initialBalanceHistorySlice = ({
  prefix = 'shared',
  sliceName = 'balanceHistory',
  commands = BalanceHistoryCommands,
  messages,
  initialState,
}: SliceFactoryPropsWithMessages<BalanceHistoryState, typeof BalanceHistoryCommands>) => {
  if (!initialState) {
    throw new Error('Invalid initial state');
  }
  const allowedCommands = Object.values(commands);

  return createSlice({
    name: `${prefix}/${sliceName}`,
    // retainers.balanceHistory
    initialState,
    reducers: {
      setInitialState(state, action: PayloadAction<Partial<BalanceHistoryState>>) {
        if (action.payload.startDate && dayjs().isSameOrAfter(action.payload.startDate)) {
          state.startDate = action.payload.startDate ?? initialState.startDate;
          // Not getComparisonTimeOptionValue: `current` is an end time only.
          state.startTime = dayjs(action.payload.startDate).format('HH:mm');
        }
        if (action.payload.endDate && dayjs().isSameOrAfter(action.payload.endDate)) {
          state.endDate = action.payload.endDate ?? initialState.endDate;
          // The end snapshot follows the date: otherwise a past range would keep `current` (the
          // SKY-5067 bug) and be shown with the live date and price. A range ending today arrives as
          // "now", not as the 23:59 getComparisonTimeOptionValue reads as `current`.
          state.endTime = isComparisonDateNow(action.payload.endDate)
            ? 'current'
            : getComparisonTimeOptionValue(action.payload.endDate);
        }
      },

      setComparisonRange(state, action: PayloadAction<{ startDate?: string; endDate?: string }>) {
        // An unchanged end keeps its snapshot: a range restored as ending "now" holds `current`,
        // which that date alone cannot tell from a snapshot picked at the same time today. Only
        // while that end is still today: past midnight `current` would show a past range as live.
        const keepsEndSnapshot =
          action.payload.endDate === state.endDate &&
          !!action.payload.endDate &&
          isComparisonDateNow(action.payload.endDate);

        state.startDate = action.payload.startDate;
        state.endDate = action.payload.endDate;

        if (action.payload.startDate) {
          state.startTime = getComparisonTimeOptionValue(action.payload.startDate);
        } else {
          state.startTime = '00:00';
        }

        if (!action.payload.endDate) {
          state.endTime = 'current';
        } else if (!keepsEndSnapshot) {
          state.endTime = getComparisonTimeOptionValue(action.payload.endDate);
        }
      },

      openSetInitialBalanceModal(state, action: PayloadAction<SetObjInitialBalanceFormValues>) {
        state.isInitialBalanceModalOpen = true;
        state.formSetInitialBalance = action.payload;
        state.resSetInitialBalance = null;
      },

      closeSetInitialBalanceModal(state, action: PayloadAction<object>) {
        console.debug('closeSetInitialBalanceModal action:', action);
        state.isInitialBalanceModalOpen = false;
        state.formSetInitialBalance = undefined;
      },
    },
    extraReducers: (builder) => {
      // listen

      builder.addCase(getBalanceHistoryWs, (state /* , action */) => {
        // const { section } = action.meta.arg
        state.requestStatus = 'pending';
      });

      builder.addCase(setInitialBalance, (state /* , action */) => {
        // const { section } = action.meta.arg
        state.resSetInitialBalance = 'pending';
      });

      // on WS message

      builder.addCase(messages, (state, action) => {
        const { json, skipProcessing, error, errorCommand } = parseResponse(
          action.payload,
          allowedCommands,
        );

        if (skipProcessing || !json) return;

        console.debug(
          `${prefix}/${sliceName}/processing ${messages.toString()}`,
          action,
          allowedCommands,
        );

        const { command, okCommand } = json;

        if (error) {
          state.error = error;
          switch (errorCommand) {
            case BalanceHistoryCommands.SetInitialBalance:
              state.resSetInitialBalance = 'error';
              break;
            case undefined:
            default:
          }
          return;
        }

        if (command === BalanceHistoryCommands.PriceHistory) {
          const { list } = json as JsonPriceHistorySuccess;
          if (list) {
            state.priceHistory = list;
          }
          return;
        }

        if (command === BalanceHistoryCommands.Prices) {
          const { list } = json as JsonPriceHistorySuccess;
          if (list) {
            state.prices = list;
          }
          return;
        }

        if (command === BalanceHistoryCommands.BalanceHistory) {
          const { regular, overrides, initialBalances } = json as JsonBalanceHistorySuccess;
          state.regular = regular;
          state.overrides = overrides;
          state.initialBalances = initialBalances;
          state.requestStatus = 'success';

          return;
        }

        if (isOkCommand(command)) {
          switch (okCommand) {
            // case 'Projects':
            //   state.list = json.list;
            //   break;
            case BalanceHistoryCommands.SetInitialBalance:
              state.resSetInitialBalance = 'success';
              state.isInitialBalanceModalOpen = false;
              break;

            default:
          }
        }
      });
    },
  });
};
